<script setup lang="ts">
import {computed} from 'vue';
import ProgramDiscover from '@/vue/molecules/today/program-discover.vue';
import ProgramRow from '@/vue/molecules/today/program-row.vue';
import type {ProgramCatalogue, TileProgram} from '@/ts/types/component/today-page';

/**
 * One catalogue inside the tile: its label, then either the user's programs in
 * it or an invitation to browse it. Both cases keep the label, so the two
 * catalogues stay aligned however full each one is.
 */
const props = defineProps<{
  catalogue: ProgramCatalogue;
  programs: TileProgram[];
}>();

/** Passed straight up (IPD-2974): the block knows what was pressed, the page knows what it means. */
const emit = defineEmits<{
  open: [program: TileProgram];
  browse: [catalogue: ProgramCatalogue];
}>();

// Literal keys, not built from the catalogue name: `no-missing-keys` only sees
// literal arguments, so a dynamic key would let a rename slip through lint and
// CI silently.
const label = computed(() => (props.catalogue === 'medical'
  ? 'program_tile_medical_label'
  : 'program_tile_prevention_label'));
</script>

<template>
  <section class="pcb">
    <h3 class="pcb-label">
      {{ $t(label) }}
    </h3>
    <div class="pcb-rows">
      <program-row
        v-for="program in programs"
        :key="program.interventionId"
        :program="program"
        @open="emit('open', $event)"
      />
      <program-discover
        v-if="programs.length === 0"
        :catalogue="catalogue"
        @browse="emit('browse', $event)"
      />
    </div>
  </section>
</template>

<style scoped lang="scss">
.pcb {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.pcb-label {
  margin: 0;
  font-family: var(--Font-Primary-Font);
  font-size: 10px;
  font-weight: 800;
  // Pinned: the inherited ratio makes the label box 14px tall and pushes every
  // row 4px down against the design.
  line-height: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--Neutral-UI-neutral-ui-100);
}

.pcb-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
